import {
  ComponentType,
  forwardRef,
  lazy,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { VideoElementProps } from 'react-player/types';
import {
  CAPTION_RETRY_DELAYS,
  CaptionApi,
  CaptionTrack,
  showCaptions,
  suppressCaptions,
} from './captions';

/**
 * The <youtube-video> wrapper react-player would load itself, kept in its own
 * chunk the same way. Its published types are generated from the DOM interface:
 * they expect `autoplay` where React passes `autoPlay` and they pin
 * `cc_load_policy` to the literal 1, so react-player's prop shape — which is
 * what actually arrives here — is the honest annotation.
 */
const YouTubeVideoElement = lazy(
  () => import('youtube-video-element/react'),
) as unknown as ComponentType<VideoElementProps>;

/** The mounted element; `api` is the YT.Player youtube-video-element creates. */
type PlayerElement = HTMLVideoElement & { api?: CaptionApi };

/** What react-player hands us as `config.youtube`, as far as captions go. */
interface CaptionConfig {
  /** 1 forces captions on; 0 leaves it to the viewer's own YouTube setting. */
  cc_load_policy?: number;
  /** Which language to show them in — YouTube's own parameter name. */
  cc_lang_pref?: string;
}

/**
 * <youtube-video> for react-player, with our caption policy guaranteed to
 * arrive in time (#1710).
 *
 * The element builds its iframe URL from `attributeChangedCallback('src')`, one
 * microtask later, out of `{...defaults, ...this.config}`. Its own default is
 * `cc_load_policy: 1`, which does not mean "no preference" but *forces*
 * captions on, automatic ones included. React sets the `src` attribute while it
 * creates the element, during the render phase, whereas the wrapper only
 * assigns the `config` property from a layout effect at commit time — and React
 * is free to yield to the browser in between. When it does, the microtask wins,
 * the URL is already serialized and our `cc_load_policy` is gone. Hence the
 * flakiness #1710 reported: the same page showed automatic captions on one load
 * and not on the next.
 *
 * So the element is created without a `src` and only gets one a render later.
 * That leaves the wrapper's layout effect nothing to race: by the time there is
 * a URL to serialize, `config` has been handed over. Passive effects always run
 * after layout effects, so this holds however React schedules the work.
 *
 * The `controls` attribute does make the element call `load()` on that first
 * render, but it bails out on `if (!this.src)` before serializing anything or
 * dispatching `loadstart`. The only trace it leaves is that the second `load()`
 * no longer counts as the first one, which just skips re-reading `config` off a
 * server-rendered iframe — and Embed never renders the player on the server.
 *
 * Getting the right `cc_load_policy` through is only half of it, in either
 * direction — see ./captions for why both need the player API as well.
 */
const YouTubePlayer = forwardRef<HTMLVideoElement, VideoElementProps>(
  ({ src, ...props }, ref) => {
    const [configured, setConfigured] = useState(false);
    const [player, setPlayer] = useState<PlayerElement | null>(null);

    useEffect(() => {
      setConfigured(true);
    }, []);

    // A callback ref, because with `light` the element only appears once the
    // viewer clicks the preview — a plain ref would not re-run the effect.
    const attachRef = useCallback(
      (node: PlayerElement | null) => {
        setPlayer(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const config = props.config as CaptionConfig | undefined;
    const captionsWanted = config?.cc_load_policy === 1;
    const captionLanguage = config?.cc_lang_pref;

    useEffect(() => {
      if (!player) return;

      const timeouts: ReturnType<typeof setTimeout>[] = [];
      // Both directions need the same ladder: YouTube loads its captions module
      // a moment after the video, and fills the tracklist later still.
      const step = captionsWanted
        ? () =>
            showCaptions(
              player.textTracks as unknown as Iterable<CaptionTrack>,
              captionLanguage,
            )
        : () => suppressCaptions(player.api);

      // `once`: after the retries have run, whatever the viewer chooses stands.
      const onPlay = () => {
        CAPTION_RETRY_DELAYS.forEach((delay) => {
          timeouts.push(setTimeout(step, delay));
        });
      };

      player.addEventListener('play', onPlay, { once: true });
      return () => {
        player.removeEventListener('play', onPlay);
        timeouts.forEach(clearTimeout);
      };
    }, [player, captionsWanted, captionLanguage, src]);

    return (
      <YouTubeVideoElement
        {...props}
        src={configured ? src : undefined}
        ref={attachRef}
      />
    );
  },
);

export default YouTubePlayer;
